import type * as React from 'react';

import { cn } from '~utils/cn';

type SpinnerProps = React.ComponentProps<'span'> & {
  /** Visual size of the spinner. */
  size?: 'sm' | 'default' | 'lg';
  /** Accessible loading label for screen readers. */
  label?: string;
  /** Hides the spinner from assistive technology when surrounding content already communicates loading. */
  decorative?: boolean;
};

const SPINNER_SIZE_CLASSES: Record<NonNullable<SpinnerProps['size']>, string> = {
  sm: 'size-4',
  default: 'size-5',
  lg: 'size-6',
};

function Spinner({ className, size = 'default', label = 'Loading', decorative = false, ...props }: SpinnerProps) {
  const accessibilityProps = decorative ? { 'aria-hidden': true } : { role: 'status', 'aria-label': label };

  return (
    <span
      data-slot="spinner"
      className={cn(
        'inline-block shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent text-primary motion-reduce:animate-none',
        SPINNER_SIZE_CLASSES[size],
        className,
      )}
      {...accessibilityProps}
      {...props}
    />
  );
}

export { Spinner };
